import { Anchor, Badge, Text } from '@mantine/core';
import { Link } from 'react-router-dom';
import { MRT_ColumnDef } from 'mantine-react-table';
import { type ClaimQueryRecord } from '@billing/validation/claimQuery';
import { formatDateOnly, formatDateTime } from 'utils/dateUtils';
import { CLAIMS_WINDOW_TARGET } from 'utils/crossTabNavigation';

const QUERY_STATUS_COLORS: Record<string, string> = {
  CREATED: 'gray',
  SUBMITTED: 'blue',
  PENDING: 'yellow',
  COMPLETED: 'green',
  FAILED: 'red',
};

export const formatClaimQueryPersonName = (
  person?: {
    firstName?: string | null;
    lastName?: string | null;
  } | null
) => [person?.firstName, person?.lastName].filter(Boolean).join(' ').trim();

export const formatClaimQueryProviderName = (
  provider?: {
    title?: string | null;
    firstName?: string | null;
    lastName?: string | null;
  } | null
) =>
  [provider?.title, provider?.firstName, provider?.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();

export const formatClaimQueryTariffs = (claim?: ClaimQueryRecord['claim']) =>
  (claim?.tariffs ?? [])
    .map(({ tariff }) => `${tariff.prefix}${tariff.code}`)
    .join(', ');

export const renderClaimQueryStatusBadge = (status: string) => (
  <Badge color={QUERY_STATUS_COLORS[status] ?? 'gray'} variant="light">
    {status}
  </Badge>
);

type Translate = (key: string, defaultMessage: string) => string;

export const ClaimQueryColumns = (
  timezone?: string | null,
  translate: Translate = (_key, defaultMessage) => defaultMessage,
  canViewClaims = false
) => {
  const columns: MRT_ColumnDef<ClaimQueryRecord>[] = [
    {
      id: 'claim.claimNumber',
      header: 'Claim #',
      accessorFn: (row) => row.claim?.claimNumber ?? '',
      filterVariant: 'text',
      mantineFilterTextInputProps: {
        placeholder: 'Search claim #',
      },
      Cell: ({ row }) => {
        const claim = row.original.claim;
        const claimNumber = claim?.claimNumber;
        if (!claimNumber) return '—';
        if (!canViewClaims) {
          return claimNumber;
        }

        return (
          <Anchor
            component={Link}
            to={`/claims/edit/${encodeURIComponent(claim.id)}`}
            target={CLAIMS_WINDOW_TARGET}
            size="sm"
            onClick={(event) => event.stopPropagation()}
            aria-label={
              translate('pages:queries.table.openClaim', 'Open claim') +
              ` ${claimNumber}`
            }
          >
            {claimNumber}
          </Anchor>
        );
      },
    },
    {
      id: 'claim.patient',
      header: translate('pages:queries.table.patientName', 'Patient Name'),
      accessorFn: (row) => formatClaimQueryPersonName(row.claim?.patient),
      filterVariant: 'text',
      mantineFilterTextInputProps: {
        placeholder: translate(
          'pages:queries.table.searchPatient',
          'Search patient'
        ),
      },
      Cell: ({ row }) =>
        formatClaimQueryPersonName(row.original.claim?.patient) || '—',
    },
    {
      id: 'claim.healthcard.number',
      header: translate('pages:queries.table.phin', 'PHIN'),
      accessorFn: (row) => row.claim?.healthcard?.number ?? '',
      filterVariant: 'text',
      mantineFilterTextInputProps: {
        placeholder: translate('pages:queries.table.searchPhin', 'Search PHIN'),
      },
      Cell: ({ row }) => row.original.claim?.healthcard?.number || '—',
    },
    {
      id: 'claim.provider',
      header: translate('pages:queries.table.provider', 'Provider'),
      accessorFn: (row) => formatClaimQueryProviderName(row.claim?.provider),
      filterVariant: 'text',
      mantineFilterTextInputProps: {
        placeholder: translate(
          'pages:queries.table.searchProvider',
          'Search provider'
        ),
      },
      Cell: ({ row }) =>
        formatClaimQueryProviderName(row.original.claim?.provider) || '—',
    },
    {
      id: 'claim.serviceStart',
      header: translate('pages:queries.table.serviceDate', 'Service Date'),
      accessorFn: (row) =>
        row.claim?.serviceStart
          ? formatDateOnly(row.claim.serviceStart, { timezone })
          : '',
      filterVariant: 'date-range',
      mantineFilterDateInputProps: {
        valueFormat: 'YYYY-MM-DD',
        clearable: true,
      },
      Cell: ({ row }) =>
        row.original.claim?.serviceStart
          ? formatDateOnly(row.original.claim.serviceStart, { timezone })
          : '—',
    },
    {
      id: 'claim.tariffs',
      header: translate('pages:queries.table.tariff', 'Tariff'),
      accessorFn: (row) => formatClaimQueryTariffs(row.claim),
      filterVariant: 'text',
      enableSorting: false,
      mantineFilterTextInputProps: {
        placeholder: translate(
          'pages:queries.table.searchTariff',
          'Search tariff'
        ),
      },
      Cell: ({ row }) => formatClaimQueryTariffs(row.original.claim) || '—',
    },
    {
      id: 'createdByUser',
      header: 'Created By',
      accessorFn: (row) => {
        return (
          formatClaimQueryPersonName(row.createdByUser) ||
          row.createdByUser?.email ||
          ''
        );
      },
      filterVariant: 'text',
      mantineFilterTextInputProps: {
        placeholder: 'Search creator',
      },
      Cell: ({ row }) =>
        row.original.createdByUser ? (
          <Text>
            {formatClaimQueryPersonName(row.original.createdByUser) ||
              row.original.createdByUser.email}
          </Text>
        ) : (
          '—'
        ),
    },
    {
      accessorKey: 'status',
      id: 'status',
      header: 'Status',
      filterVariant: 'select',
      mantineFilterSelectProps: {
        data: ['CREATED', 'SUBMITTED', 'PENDING', 'COMPLETED', 'FAILED'],
      },
      Cell: ({ row }) => renderClaimQueryStatusBadge(row.original.status),
    },
    {
      accessorKey: 'queryType',
      id: 'queryType',
      header: 'Query Type',
      filterVariant: 'select',
      mantineFilterSelectProps: {
        data: ['PR', 'IP', 'WQ', 'IB', 'OT', 'FA'],
      },
    },
    {
      accessorKey: 'createdAt',
      id: 'createdAt',
      header: 'Created',
      filterVariant: 'date-range',
      Cell: ({ row }) => formatDateTime(row.original.createdAt),
    },
    {
      accessorKey: 'lastPolledAt',
      id: 'lastPolledAt',
      header: 'Last Polled',
      enableColumnFilter: false,
      Cell: ({ row }) =>
        row.original.lastPolledAt
          ? formatDateTime(row.original.lastPolledAt)
          : '—',
    },
  ];

  return columns;
};
